<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-kaia-logo="false"
    :show-lang-picker="false"
  >
    <template #header_start><ButtonBack @click="emit('back')" /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">{{ $t(question.headerKey) }}</p>
        <p
          v-if="question.descriptionKey"
          class="kds-typography-paragraph-large"
        >
          {{ $t(question.descriptionKey) }}
        </p>
      </div>

      <!-- Name the goal back to the user, so "did you reach it?" has a subject.
           Icon, purple disc and copy are the ones SelfTestPreSignupComplete
           already uses for its goal tile. -->
      <div
        v-if="goalLabelKey"
        class="renewal-goal-card"
        data-qa="goal-card"
      >
        <div class="renewal-goal-card__icon">
          <img
            :src="goalIcon"
            alt=""
          />
        </div>
        <div>
          <p class="kds-typography-paragraph-large kds-color-text-tertiary">
            {{ $t('eu__renewal_assessment__goal_achievement__goal_label') }}
          </p>
          <p class="kds-typography-title-large">{{ $t(goalLabelKey) }}</p>
        </div>
      </div>

      <fieldset class="renewal-therapy-feedback__options">
        <div
          v-for="option in question.options"
          :key="option.key"
          class="quiz-radio"
          :class="{ 'quiz-radio--checked': selectedKey === option.key }"
          :data-qa="`option-${option.key}`"
          @click="select(option)"
        >
          <label
            class="quiz-radio__label renewal-therapy-feedback__label"
            :for="inputId(option)"
          >
            <span class="kds-typography-ui-strong-large">
              {{ $t(option.label) }}
            </span>
            <span class="kds-typography-ui-subtle-medium">
              {{ $t(option.description) }}
            </span>
          </label>
          <div class="quiz-radio__layout">
            <!-- CustomRadio's `name` prop is what it puts on the input's id, so
                 this is what the label above points at. -->
            <CustomRadio
              :checked="selectedKey === option.key"
              :name="inputId(option)"
              :value="option.key"
            />
          </div>
        </div>
      </fieldset>
    </div>

    <template #actions>
      <BaseButton
        :text="$t('generic_action_continue')"
        :disabled="selectedKey == null"
        data-qa="submit"
        @click="handleNextEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import i18n from '@shared/localization'
import ROUTE from '@shared/modules/assessment/router/names'
import useRenewalAssessmentStore from '@shared/modules/assessment/stores/useRenewalAssessmentStore'
import {
  GOAL_ACHIEVEMENT_OPTIONS,
  THERAPY_HELPFULNESS_OPTIONS,
} from '@shared/modules/assessment/config/renewalAssessmentOptions.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import CustomRadio from '@shared/components/CustomRadio.vue'
import goalIcon from '@shared/assets/img/program_overview_goal.svg'

defineProps({
  progress: { type: Number, default: null },
  handleNextEvent: { type: Function, default: () => {} },
})

const emit = defineEmits(['back'])

const route = useRoute()
const renewalAssessmentStore = useRenewalAssessmentStore()

const isGoalAchievement = computed(
  () => route.name === ROUTE.ASSESSMENT_RENEWAL_GOAL_ACHIEVEMENT,
)

// The two questions of the renewal_therapy_feedback questionnaire. Only one is
// ever asked — the goal branch decides which — and they are the same three-option
// screen, so they share a component. PGIC is its own screen: it belongs to a
// different questionnaire and is a seven-point scale.
const question = computed(() =>
  isGoalAchievement.value
    ? {
        headerKey: 'eu__renewal_assessment__goal_achievement__header',
        // no description line — the goal card below carries the context
        descriptionKey: null,
        options: GOAL_ACHIEVEMENT_OPTIONS,
      }
    : {
        headerKey: 'eu__renewal_assessment__therapy_feedback__header',
        descriptionKey: 'eu__renewal_assessment__therapy_feedback__description',
        options: THERAPY_HELPFULNESS_OPTIONS,
      },
)

const answer = computed({
  get: () =>
    isGoalAchievement.value
      ? renewalAssessmentStore.goalAchievement
      : renewalAssessmentStore.therapyHelpfulness,
  set: (value) => {
    if (isGoalAchievement.value) {
      renewalAssessmentStore.goalAchievement = value
    } else {
      renewalAssessmentStore.therapyHelpfulness = value
    }
  },
})

const selectedKey = computed(() => answer.value?.key ?? null)

// Only the goal question names a goal; the therapy fallback has none to show.
const goalLabelKey = computed(() =>
  isGoalAchievement.value ? renewalAssessmentStore.goalLabelKey : null,
)

// Option keys are plain words like `helped`, and an id is document-wide, so
// namespace them before they become one.
const inputId = (option) => `renewal-answer-${option.key}`

// Store the label alongside the key: the API submits the copy the user saw.
const select = (option) => {
  answer.value = { key: option.key, label: i18n.global.t(option.label) }
}
</script>

<style lang="scss" scoped>
// Same icon disc as SelfTestPreSignupComplete's goal tile, in a bordered card.
.renewal-goal-card {
  display: flex;
  align-items: center;
  gap: $kds-space-1_5x;
  padding: $kds-space-2x;
  border: 1px solid $kds-color-background-blue-accent;
  border-radius: $kds-space-1x;

  &__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    overflow: hidden;
    background-color: $kds-color-background-purple-strong;
  }
}

.renewal-therapy-feedback {
  &__options {
    display: flex;
    flex-direction: column;
    gap: $kds-space-1x;

    .quiz-radio {
      margin: 0;
    }
  }

  // .quiz-radio__label is a single-line label by default; these options stack a
  // description under the title.
  &__label {
    display: flex;
    flex-direction: column;
    gap: $kds-space-0_5x;
  }
}
</style>
